import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ContactDrawer } from "./ContactDrawer";
import { ContactCreateDialog } from "./ContactCreateDialog";
import { ContactEditDialog } from "./ContactEditDialog";
import { useContacts, CONTACTS_PAGE_SIZE as PAGE_SIZE } from "@/hooks/use-contacts";
import { apiClient } from "@/lib/api-client";
import { Users, Plus, Search, Phone, Mail, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import type { Contact } from "@/api/generated/client/models";

interface ContactsTabProps {
    openDetailId?: number | null;
    onDetailConsumed?: () => void;
}

export function ContactsTab({ openDetailId, onDetailConsumed }: ContactsTabProps = {}) {
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [page, setPage] = useState(1);
    const [showCreate, setShowCreate] = useState(false);
    const [editContact, setEditContact] = useState<Contact | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Contact | null>(null);
    const [detailContact, setDetailContact] = useState<Contact | null>(null);
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Debounce search to avoid a request on every keystroke
    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 300);
        return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
    }, [search]);

    const { contacts, total, totalPages, isLoading, isPlaceholderData, createContact, updateContact, deleteContact, isCreating, isUpdating } = useContacts({ search: debouncedSearch, page });

    // Open detail drawer when navigated from another tab — fetch by ID if not in current page
    useEffect(() => {
        if (openDetailId == null) return;
        const c = contacts.find((ct: Contact) => ct.id === openDetailId);
        if (c) {
            setDetailContact(c);
            onDetailConsumed?.();
        } else if (!isLoading) {
            // Entity not in current page/search — fetch directly
            apiClient.contacts.getContact({ id: openDetailId }).then((fetched) => {
                setDetailContact(fetched);
                onDetailConsumed?.();
            }).catch(() => {
                // If fetch fails (deleted, permission, etc.), silently consume
                onDetailConsumed?.();
            });
        }
    }, [openDetailId, contacts, isLoading, onDetailConsumed]);

    // Clamp page when total shrinks (e.g. deletion reduces page count)
    useEffect(() => {
        if (totalPages > 0 && page > totalPages) {
            setPage(totalPages);
        }
    }, [totalPages, page]);

    const handleDelete = () => {
        if (!deleteTarget) return;
        deleteContact(deleteTarget.id);
        setDeleteTarget(null);
    };

    return (
        <TooltipProvider delayDuration={300}>
            <div className="space-y-4">
                {/* Toolbar */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="relative w-full sm:max-w-xs">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input placeholder="Search contacts..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" />
                    </div>
                    <Button size="sm" className="w-full sm:w-auto" onClick={() => setShowCreate(true)}>
                        <Plus className="h-4 w-4 mr-1.5" />
                        Add Contact
                    </Button>
                </div>

                {/* Content */}
                {isLoading ? (
                    <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}</div>
                ) : contacts.length === 0 ? (
                    <Card className="border-dashed">
                        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                            <div className="rounded-full bg-muted p-4 mb-4">
                                <Users className="h-8 w-8 text-muted-foreground" />
                            </div>
                            <h3 className="text-lg font-medium mb-1">No contacts found</h3>
                            <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                                {search ? "Try a different search term." : "Add your first contact to start building your directory."}
                            </p>
                            {!search && (
                                <Button size="sm" onClick={() => setShowCreate(true)}>
                                    <Plus className="h-4 w-4 mr-1.5" />Add Contact
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                ) : (
                    <Card className="overflow-hidden">
                        <Table className="min-w-[760px] md:min-w-0 table-fixed">
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[20%]">Name</TableHead>
                                    <TableHead className="w-[15%]">Type</TableHead>
                                    <TableHead className="w-[15%]">Job Title</TableHead>
                                    <TableHead className="w-[18%]">Phone</TableHead>
                                    <TableHead className="md:hidden lg:table-cell w-[27%]">Email</TableHead>
                                    <TableHead className="w-[40px]"><span className="sr-only">Actions</span></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {contacts.map((c: Contact) => (
                                    <TableRow key={c.id} tabIndex={0} className="group hover:bg-muted/50 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset" onClick={() => setDetailContact(c)} onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setDetailContact(c); } }}>
                                        <TableCell className="font-medium truncate">
                                            {c.firstName} {c.lastName}
                                        </TableCell>
                                        <TableCell className="truncate">
                                            {c.contactType ? c.contactType : <span className="text-muted-foreground/40">—</span>}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground truncate">
                                            {c.jobTitle ? c.jobTitle : <span className="text-muted-foreground/40">—</span>}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground truncate">
                                            {c.phone ? (
                                                <span className="flex items-center gap-1.5 text-sm truncate"><Phone className="h-3.5 w-3.5 shrink-0" />{c.phone}</span>
                                            ) : <span className="text-muted-foreground/40">—</span>}
                                        </TableCell>
                                        <TableCell className="md:hidden lg:table-cell text-muted-foreground truncate">
                                            {c.email ? (
                                                <span className="flex items-center gap-1.5 text-sm truncate"><Mail className="h-3.5 w-3.5 shrink-0" />{c.email}</span>
                                            ) : <span className="text-muted-foreground/40">—</span>}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" aria-label="Delete contact" onClick={(e) => { e.stopPropagation(); setDeleteTarget(c); }}>
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>Delete</TooltipContent>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <div className="px-4 py-3 border-t flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">
                                {total} contact{total !== 1 ? "s" : ""}
                                {totalPages > 1 && ` · Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)}`}
                            </span>
                            {totalPages > 1 && (
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1 || isPlaceholderData}>
                                        <ChevronLeft className="h-4 w-4 mr-1" />Previous
                                    </Button>
                                    <span className="text-xs text-muted-foreground px-2">Page {page} of {totalPages}</span>
                                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages || isPlaceholderData}>
                                        Next<ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    </Card>
                )}

                {/* Dialogs */}
                <ContactCreateDialog
                    open={showCreate}
                    onClose={() => setShowCreate(false)}
                    createContact={createContact}
                    isCreating={isCreating}
                />

                <ContactEditDialog
                    contact={editContact}
                    onClose={() => setEditContact(null)}
                    updateContact={updateContact}
                    isUpdating={isUpdating}
                />

                {/* Delete Confirmation */}
                <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Delete contact?</AlertDialogTitle>
                            <AlertDialogDescription>
                                This will permanently delete <span className="font-medium text-foreground">{deleteTarget?.firstName} {deleteTarget?.lastName}</span> and remove them from any linked claims. This action cannot be undone.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                Delete
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>

            <ContactDrawer
                contact={detailContact}
                open={!!detailContact}
                onClose={() => setDetailContact(null)}
                onEdit={(c) => { setDetailContact(null); setEditContact(c); }}
            />
        </TooltipProvider>
    );
}
